<template>
  <div class="relative">
    <button
      v-if="selectedLocale"
      ref="regionMenuButton"
      type="button"
      aria-haspopup="true"
      :aria-label="$t('app.choose-your-locale')"
      data-testid="change-country"
      :disabled="isReadOnly"
      class="flex items-center justify-start group transition"
      :class="[textClass, buttonClass]"
      @click.prevent="toggleOptions"
      @keydown.prevent="toggleOptions"
    >
      <img
        :src="selectedLocale.img_url"
        :alt="selectedLocale.country_code"
        class="w-6 rounded mr-2 group-hover:opacity-75 transition"
      />
      {{ selectedLocale.country_name }}
      <fa
        v-if="!disabled"
        :icon="['fal', open ? 'angle-up' : 'angle-down']"
        class="ml-auto text-lg"
      />
    </button>
    <transition
      v-cloak
      enter-active-class="transition ease-out duration-100"
      enter-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="open"
        ref="regionMenu"
        class="origin-top-left absolute left-0 mt-2 w-56 rounded-md shadow-lg"
        :class="isRebranded ? 'w-full z-999' : 'z-50'"
      >
        <div
          class="rounded-md bg-white ring-1 ring-black/5 relative"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="region-menu"
        >
          <div
            class="space-y-2.5 px-4 py-2"
            @click="clearSession"
            @keydown="clearSession"
          >
            <a
              v-for="site in getLocalLinks.sites"
              :key="site.text"
              :href="site.href"
              :lang="site.lang"
              rel="nofollow"
              :data-testid="`change-locale-to-${site.locale_code}`"
              class="flex items-center justify-start transition cursor-pointer group text-neutral-900 hover:text-ocean-500"
              :data-is-global="!!site.is_global"
              @click="selectSiteLocale(site, $event)"
            >
              <img
                :src="site.img_url"
                class="w-6 rounded mr-2 group-hover:opacity-75 transition"
                :alt="site.text"
              />
              <span>{{ site.text }}</span>
              <span
                v-if="isLocaleSelected(site)"
                class="flex-1 flex justify-end"
              >
                <fa
                  :icon="['fal', 'check']"
                  class="text-ocean-700 text-lg"
                />
              </span>
            </a>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { getCookie } from '@/helpers/cookies';
import {
  i18n,
  setLocale,
} from '@/i18n';
import ClickAway from '@/mixins/ClickAway';
import ManagesClientSession from '@/mixins/ManagesClientSession';
import ManagesLocaleCookies from '@/mixins/ManagesLocaleCookies';

export default {
  name: 'VetsterLocaleChanger',

  mixins: [
    ClickAway,
    ManagesClientSession,
    ManagesLocaleCookies,
  ],

  props: {
    isPageReload: {
      type: Boolean,
      default: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    isRebranded: {
      default: false,
      type: Boolean,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    textClass: {
      type: String,
      default: 'text-sm',
    },
  },

  data() {
    return {
      open: false,
      selectedLocale: null,
    };
  },

  computed: {
    ...mapGetters(['getLocalLinks']),
    buttonClass() {
      const classes = ['text-neutral-900'];

      // Cursor class
      if (this.disabled) {
        classes.push('cursor-default');
      } else if (this.isReadOnly) {
        classes.push('cursor-not-allowed');
      } else {
        classes.push('cursor-pointer');
      }

      if (this.disabled) {
        classes.push('space-x-6');
      } else if (this.isRebranded) {
        classes.push(
          'bg-white',
          'border',
          'border-neutral-200',
          'placeholder:text-base',
          'ring-neutral-200',
          'focus:ring-ocean-600',
          'text-neutral-900',
          'rounded-lg',
          'h-11',
          'px-3',
          'w-full',
        );
      } else {
        classes.push(
          'space-x-6',
          'hover:text-ocean-500',
          'group-focus:text-ocean-300',
        );
      }

      return classes.join(' ');
    },
  },

  watch: {
    getLocalLinks() {
      this.setCurrentLocale();
    },
  },

  mounted() {
    this.setCurrentLocale();

    document.addEventListener('click', this.closeMenu);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
  },

  methods: {
    close() {
      this.open = false;
    },
    closeMenu(event) {
      this.hideOnClickAway(event, this.$refs.regionMenu, this.close, this.$refs.regionMenuButton);
    },
    isGlobal() {
      return getCookie('v-global') === 'true';
    },
    isLocaleSelected(site) {
      if (this.isPageReload) {
        return site.selected;
      }

      return i18n.locale === site.locale_code && this.isGlobal() === !!site.is_global;
    },
    selectSiteLocale(site, $event) {
      this.selectLocaleOption(site.locale_code, $event);

      if (!this.isPageReload) {
        $event.preventDefault();
        this.setSiteLocale(site);
      }
    },
    setCurrentLocale() {
      if (!this.selectedLocale && this.getLocalLinks.current_country_code) {
        this.selectedLocale = {
          img_url: this.getLocalLinks.current_img_url,
          country_code: this.getLocalLinks.current_country_code,
          country_name: this.getLocalLinks.current_country_name,
        };
      }
    },
    setSiteLocale(site) {
      setLocale(site.locale_code);
      this.open = false;

      this.selectedLocale = {
        img_url: site.img_url,
        country_code: site.country_code,
        country_name: site.text,
      };
    },
    toggleOptions() {
      if (!this.disabled) {
        this.open = !this.open;
      }
    },
  },
};
</script>
